import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Trophy, ArrowLeft, Calendar, FileText, Check, X, Trash2 } from 'lucide-react';

export default function UserScoresTab({ 
  selectedUser, 
  userScores, 
  loadingScores,
  onBack,
  onRefresh
}) {
  const [editingScore, setEditingScore] = useState(null);
  const [editForm, setEditForm] = useState({ newPoints: '', newDivision: '' });

  const handleEditScore = (score) => {
    setEditingScore(score);
    setEditForm({
      newPoints: score.points?.toString() || '',
      newDivision: score.division
    });
  };

  const handleCancelEdit = () => {
    setEditingScore(null);
    setEditForm({ newPoints: '', newDivision: '' });
  };

  const handleSaveScore = async () => {
    if (!editingScore) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/scores/${editingScore.scorecard_id}`,
        {
          division: editForm.newDivision,
          new_points: parseInt(editForm.newPoints) || 0
        }
      );
      toast.success('Score updated successfully');
      handleCancelEdit();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update score');
    }
  };

  const handleDeleteScore = async (score) => {
    if (!window.confirm(`Delete this score entry? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/admin/users/${selectedUser.id}/scores/${score.scorecard_id}`,
        { data: { division: score.division } }
      );
      toast.success('Score deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete score');
    }
  };

  return (
    <div>
      <Button
        onClick={onBack}
        variant="ghost"
        className="mb-4 text-stone-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
        Back to Users
      </Button>
      
      <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
        <div className="bg-stone-50 border-b border-stone-200 p-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
            <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight">
              Scores for {selectedUser.name}
            </h2>
          </div>
        </div>
        
        {loadingScores ? (
          <div className="p-8 text-center">
            <p className="font-inter text-stone-500">Loading scores...</p>
          </div>
        ) : userScores.length === 0 ? (
          <div className="p-8 text-center">
            <Trophy className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-inter text-stone-500">No scores found for this user</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-100 border-b border-stone-200">
                <tr>
                  <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Date</th>
                  <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Division</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Points</th>
                  <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Source</th>
                  <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {userScores.map((score) => (
                  <tr key={`${score.scorecard_id}-${score.division}`} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-4 px-4 font-inter text-sm text-slate-900">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                        {score.scorecard_date || 'N/A'}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {editingScore?.scorecard_id === score.scorecard_id && editingScore?.division === score.division ? (
                        <Select value={editForm.newDivision} onValueChange={(v) => setEditForm({...editForm, newDivision: v})}>
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Race Gun">Race Gun</SelectItem>
                            <SelectItem value="PCC Optics">PCC Optics</SelectItem>
                            <SelectItem value="EDC">EDC</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="inline-block bg-amber-100 text-amber-800 font-barlow font-semibold text-xs px-3 py-1 rounded-sm uppercase">
                          {score.division}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {editingScore?.scorecard_id === score.scorecard_id && editingScore?.division === score.division ? (
                        <Input
                          type="number"
                          value={editForm.newPoints}
                          onChange={(e) => setEditForm({...editForm, newPoints: e.target.value})}
                          className="w-24 h-8 text-center font-mono"
                          min="-100"
                        />
                      ) : (
                        <span className="font-mono font-bold text-lg text-slate-900">
                          {score.points}
                          {score.is_manual && (
                            <span className="text-xs text-stone-400 font-normal ml-1">(manual)</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 font-inter text-sm text-stone-600">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                        <span className="truncate max-w-[150px]" title={score.file_name}>
                          {score.file_name || 'Manual Entry'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        {editingScore?.scorecard_id === score.scorecard_id && editingScore?.division === score.division ? (
                          <>
                            <Button
                              onClick={handleSaveScore}
                              variant="ghost"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 rounded-sm p-2"
                              title="Save"
                            >
                              <Check className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                            <Button
                              onClick={handleCancelEdit}
                              variant="ghost"
                              className="text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-sm p-2"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              onClick={() => handleEditScore(score)}
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-sm p-2"
                              title="Edit Points"
                            >
                              <Trophy className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                            <Button
                              onClick={() => handleDeleteScore(score)}
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm p-2"
                              title="Delete Score"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
